import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Plus, Search, Loader, AlertCircle, Calendar, DollarSign, Hash, FileText, Edit2, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Clock } from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'CASH' },
  { value: 'jazzcash', label: 'JazzCash' },
  { value: 'easypaisa', label: 'EasyPaisa' },
  { value: 'bank', label: 'Bank' },
];

const DATE_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'all', label: 'All' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const ActivationLog = () => {
  // Form state
  const [username, setUsername] = useState('');
  const [packageName, setPackageName] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [details, setDetails] = useState('');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [activationType, setActivationType] = useState('regular');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);
  const suggestionsRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Entries state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ today: { total_amount: 0, total_entries: 0 } });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch entries
  const fetchEntries = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const today = new Date();
      const todayStr = today.toLocaleDateString('en-CA');
      let params = {
        page,
        page_size: pageSize,
      };

      if (dateFrom || dateTo) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      } else if (dateFilter === 'today') {
        params.date = todayStr;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        params.date_from = weekAgo.toLocaleDateString('en-CA');
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        params.date_from = monthAgo.toLocaleDateString('en-CA');
      }

      if (methodFilter) params.payment_method = methodFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await api.get('/activations/', { params });
      const rows = res.data.results || res.data || [];
      setEntries(rows);
      setTotalCount(res.data.count || rows.length);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
      setEntries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, dateFrom, dateTo, methodFilter, searchQuery, pageSize]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await api.get('/activations/stats/');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries]);

  const filteredEntries = entries;

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchEntries(page);
  };

  // Compute filtered total
  const filteredTotal = filteredEntries.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  // Customer search autocomplete
  const handleUsernameChange = (value) => {
    setUsername(value);
    setSuggestions([]);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (value.length < 1) {
      setShowSuggestions(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      try {
        setSearchLoading(true);
        const res = await api.get('/activations/search-customer/', { 
          params: { username: value },
          signal: abortControllerRef.current.signal
        });
        setSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch (err) {
        // Don't show error if request was cancelled
        if (err.code !== 'ERR_CANCELED') {
          console.error('Search failed:', err);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const selectCustomer = (customer) => {
    setUsername(customer.username);
    setPackageName(customer.package_name || '');
    setAmount(customer.monthly_price ? customer.monthly_price.toString() : '');
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup: abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const resetForm = () => {
    setEditingEntry(null);
    setErrorMessage('');
    setUsername('');
    setPackageName('');
    setAmount('');
    setPaymentMethod('cash');
    setActivationType('regular');
    setPaymentStatus('');
    setDetails('');
    setDate(new Date().toLocaleDateString('en-CA'));
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setErrorMessage('');
    setUsername(entry.username || '');
    setPackageName(entry.package_name || '');
    setAmount(entry.amount ? entry.amount.toString() : '');
    setPaymentMethod(entry.payment_method || 'cash');
    setActivationType(entry.activation_type || 'regular');
    setPaymentStatus(entry.payment_status || '');
    setDetails(entry.details || '');
    setDate(entry.date || new Date().toLocaleDateString('en-CA'));
    setShowSuggestions(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getErrorMessage = (err) => {
    const data = err.response?.data;
    return data?.username?.[0]
      || data?.non_field_errors?.[0]
      || data?.detail
      || 'Failed to save entry. Please try again.';
  };

  // Submit entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !paymentStatus) return;

    try {
      setSubmitting(true);
      setErrorMessage('');
      const payload = {
        username: username.trim(),
        package_name: packageName.trim(),
        amount: parseFloat(amount) || 0,
        payment_method: paymentMethod,
        activation_type: activationType,
        payment_status: paymentStatus,
        details: details.trim(),
        date: date,
      };

      const refreshPage = editingEntry ? currentPage : 1;

      if (editingEntry) {
        await api.patch(`/activations/${editingEntry.id}/`, payload);
      } else {
        await api.post('/activations/', payload);
      }

      resetForm();

      // Refresh entries
      await fetchEntries(refreshPage);
      await fetchStats();
    } catch (err) {
      console.error('Failed to save entry:', err);
      setErrorMessage(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Activation Log</h1>
        <p className="text-sm text-gray-500">Record package activations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500">Today's Entries</p>
          <p className="text-2xl font-bold text-gray-900">{stats.today?.total_entries || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-xl shadow-sm text-white">
          <p className="text-xs text-green-100">Today's Total</p>
          <p className="text-2xl font-bold">PKR {(stats.today?.total_amount || 0).toLocaleString()}</p>
        </div>
        {stats.today?.pending_count > 0 ? (
          <Link to="/activation-payments" className="bg-amber-50 border border-amber-200 p-4 rounded-xl shadow-sm hover:bg-amber-100 transition-colors">
            <p className="text-xs text-amber-600 flex items-center gap-1"><Clock size={12} /> Pending Today</p>
            <p className="text-2xl font-bold text-amber-700">{stats.today.pending_count}</p>
            <p className="text-xs text-amber-500 mt-0.5">tap to clear payments</p>
          </Link>
        ) : (methodFilter || searchQuery || dateFilter !== 'today' || dateFrom || dateTo) && (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-200">
            <p className="text-xs text-blue-600">Page Total ({filteredEntries.length})</p>
            <p className="text-2xl font-bold text-blue-700">PKR {filteredTotal.toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Entry Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Plus size={18} className="text-blue-600" />
          {editingEntry ? `Edit Entry #${editingEntry.entry_no}` : 'New Entry'}
        </h2>
        {editingEntry && (
          <p className="text-xs text-gray-500 mb-4">Editing entry #{editingEntry.entry_no}</p>
        )}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Username with autocomplete */}
          <div className="relative" ref={suggestionsRef}>
            <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="e.g. 1city256"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
              />
              {searchLoading && (
                <Loader size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
              )}
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectCustomer(s)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                  >
                    <div className="font-medium text-sm text-gray-900">{s.username}</div>
                    <div className="text-xs text-gray-500">{s.name} — {s.package_name || 'No package'} — {s.area_name || 'N/A'}</div>
                  </button>
                ))}
              </div>
            )}
            {showSuggestions && suggestions.length === 0 && !searchLoading && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-500">
                No customers found
              </div>
            )}
          </div>

          {/* Package Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Package / SVC</label>
            <input
              type="text"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              placeholder="e.g. Fib-6"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount (PKR)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 2000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Payment Status */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Payment Status <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            {[
              { value: 'clear', label: 'Clear', color: 'green' },
              { value: 'pending', label: 'Pending', color: 'amber' },
            ].map(({ value, label, color }) => (
              <button
                key={value}
                type="button"
                onClick={() => setPaymentStatus(value)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  paymentStatus === value
                    ? color === 'green'
                      ? 'bg-green-100 border-green-400 text-green-800'
                      : 'bg-amber-100 border-amber-400 text-amber-800'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
            {!paymentStatus && (
              <span className="text-xs text-red-400 self-center ml-1">Select one</span>
            )}
          </div>
        </div>

        {/* Activation Type */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">Activation Type <span className="text-gray-400 font-normal">(for bonus calculation)</span></label>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'regular', label: 'Regular', color: 'gray' },
              { value: 'new', label: 'New Installation (+PKR 500)', color: 'green' },
              { value: 'shift', label: 'Shifted Customer (+PKR 200)', color: 'orange' },
            ].map(({ value, label, color }) => (
              <button
                key={value}
                type="button"
                onClick={() => setActivationType(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  activationType === value
                    ? color === 'green' ? 'bg-green-100 border-green-400 text-green-800'
                      : color === 'orange' ? 'bg-orange-100 border-orange-400 text-orange-800'
                      : 'bg-gray-200 border-gray-400 text-gray-800'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Details</label>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="New customer, shifted from another ISP, TXN ID last 4 digits, etc."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          {editingEntry && (
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <X size={16} />
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting || !username.trim() || !paymentStatus}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {submitting ? <Loader size={16} className="animate-spin" /> : editingEntry ? <Edit2 size={16} /> : <Plus size={16} />}
            {editingEntry ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>
      </form>

      {/* Entries Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Table header with filters */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText size={18} className="text-gray-400" />
              Entries
              <span className="text-xs text-gray-400 font-normal">({totalCount})</span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {DATE_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => { setDateFilter(f.value); setDateFrom(''); setDateTo(''); }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      dateFilter === f.value && !dateFrom && !dateTo ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-gray-400" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setDateFilter(''); }}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setDateFilter(''); }}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          {/* Payment method filter + search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setMethodFilter('')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  methodFilter === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Methods
              </button>
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setMethodFilter(m.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    methodFilter === m.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="relative sm:ml-auto">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search username, method, officer..."
                className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-8 text-center">
            <Loader className="animate-spin text-blue-600 mx-auto" size={24} />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Hash className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium">No entries found</p>
            <p className="text-sm text-gray-400">Add your first activation entry above</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">No</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Username</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Package</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Details</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">By</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-gray-700">{entry.entry_no}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{entry.username}</div>
                        {entry.customer_name && <div className="text-xs text-gray-500">{entry.customer_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-blue-600 font-medium">{entry.package_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">PKR {parseFloat(entry.amount).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {entry.payment_method}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {entry.payment_status === 'pending' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">PENDING</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">CLEAR</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.activation_type === 'new' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">NEW</span>
                        )}
                        {entry.activation_type === 'shift' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">SHIFT</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate">{entry.details || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{entry.entered_by_username || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => startEdit(entry)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit2 size={13} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredEntries.map((entry) => (
                <div key={entry.id} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400">#{entry.entry_no}</span>
                      <span className="font-medium text-gray-900">{entry.username}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-700">PKR {parseFloat(entry.amount).toLocaleString()}</span>
                      <button type="button" onClick={() => startEdit(entry)} className="text-blue-600 p-1 rounded hover:bg-blue-50">
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">{entry.package_name}</span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{entry.payment_method}</span>
                  </div>
                  {entry.details && <p className="text-xs text-gray-500 mt-1">{entry.details}</p>}
                  <div className="text-[10px] text-gray-400 mt-1">
                    {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {entry.entered_by_username && ` • by ${entry.entered_by_username}`}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <span>
                  Showing {startItem}-{endItem} of {totalCount}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-lg bg-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="px-3 py-1 text-xs text-gray-600">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 rounded border border-gray-300 bg-white text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivationLog;
